import React from 'react';
import { Phone, MessageSquare, Printer, FileText, Megaphone, CreditCard, Users } from 'lucide-react';
import V2Layout from '../components/V2Layout';
import {
  FadeUp, Stagger, StaggerItem, SectionHead, CtaGhost, PageHero, Check, RuleLabel,
  TextLink, OSWindow, BigStat, Marquee, ClosingCta,
} from '../components/V2Shared';
import { COMMS_CHANNELS } from '../data/site';

const ICONS = {
  voice: Phone,
  texting: MessageSquare,
  fax: Printer,
  forms: FileText,
  campaigns: Megaphone,
  payments: CreditCard,
  team: Users,
};

/* one patient, one timeline — the signature visual */
const TIMELINE = [
  { time: 'tue 9:48 pm', channel: 'call', by: 'Amy', text: 'Inbound call — crown came off, patient flying Friday. Booked Thu 10:20a. Recording + transcript filed.' },
  { time: 'tue 9:51 pm', channel: 'sms', by: 'Amy', text: 'Confirmation text with prep instructions and forms link.' },
  { time: 'wed 8:02 am', channel: 'forms', by: 'Morgan', text: 'Forms completed on phone · insurance card photographed · data extracted.' },
  { time: 'wed 8:09 am', channel: 'system', by: 'Amanda', text: 'Eligibility verified · crown coverage confirmed · breakdown saved to chart.' },
  { time: 'wed 8:10 am', channel: 'chat', by: 'Trisha', text: 'Team chat: “Tomorrow 10:20a re-cement fully prepped — forms in, coverage verified.”' },
  { time: 'thu 11:05 am', channel: 'sms', by: 'Kianna', text: 'Visit complete · plain-language statement + text-to-pay link sent. Settled in 4 minutes.' },
  { time: 'thu 5:30 pm', channel: 'call', by: 'Elise', text: 'Post-visit check-in · patient thrilled · review link sent.' },
];

const CHANNEL_COLORS = {
  call: 'text-[#1a46e5] bg-[#eef3ff]',
  sms: 'text-[#0f9d76] bg-[#e6f6f0]',
  forms: 'text-[#a16207] bg-[#fdf4e3]',
  system: 'text-[#7c3aed] bg-[#f3eefe]',
  chat: 'text-[#0e7490] bg-[#e6f7fb]',
};

const V2Communications = () => (
  <V2Layout title="Communication Suite — Asha">
    <PageHero
      eyebrow="The platform / communication suite"
      title={<>Every call, text, fax, and form. <span className="text-[#1a46e5]">One patient story.</span></>}
      body="Phones, two-way texting, intelligent fax, digital forms, campaigns, and team chat — built into the operating system, not bolted on beside it. Your staff and your AI agents work the same inbox, and every touchpoint lands on the patient's timeline."
    >
      <CtaGhost to="/book-demo">See the unified inbox</CtaGhost>
      <TextLink to="/v2/front-office">Meet the agents who work it</TextLink>
    </PageHero>

    <Marquee items={COMMS_CHANNELS.map((c) => c.name)} />

    {/* the timeline — signature section */}
    <section className="px-6 py-20 md:py-28">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="01 / the patient timeline" />
        <div className="mt-12 grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <div>
            <SectionHead
              eyebrow="One thread, every channel"
              title={<>This is what “unified” actually means.</>}
              body="A crown emergency, start to finish: the 9:48 pm call, the forms, the verification, the team heads-up, the payment, the review. Seven touchpoints, five channels, four AI agents — one timeline your whole team can read at a glance."
            />
            <FadeUp delay={0.1} className="mt-8">
              <ul className="space-y-2.5">
                <Check>No tab-switching between phone, text, and fax tools</Check>
                <Check>AI agents and staff visibly working the same record</Check>
                <Check>Recordings, transcripts, and documents attached in place</Check>
                <Check>Context follows the patient, not the app</Check>
              </ul>
            </FadeUp>
          </div>
          <FadeUp delay={0.12}>
            <OSWindow title="asha — patient timeline / crown re-cement">
              <div className="bg-white p-5">
                <div className="space-y-0">
                  {TIMELINE.map((t, i) => (
                    <div key={i} className="relative flex gap-4 pb-5 last:pb-0">
                      {i < TIMELINE.length - 1 && (
                        <span aria-hidden className="absolute left-[8.5px] top-5 h-full w-px bg-[#dde5f3]" />
                      )}
                      <span className={`relative mt-1 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full ${CHANNEL_COLORS[t.channel]}`}>
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2.5">
                          <span className="v2-mono text-[10px] font-semibold uppercase tracking-wider text-[#a8bcf0]">{t.time}</span>
                          <span className={`v2-mono rounded px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wider ${CHANNEL_COLORS[t.channel]}`}>
                            {t.channel}
                          </span>
                          <span className="text-[12.5px] font-bold text-[#0a1633]">{t.by}</span>
                        </div>
                        <p className="mt-1 text-[13px] leading-relaxed text-[#3f4d6e]">{t.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </OSWindow>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* channels */}
    <section className="px-6 pb-24">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="02 / the channels" />
        <div className="mt-12">
          <SectionHead
            eyebrow="Seven channels, zero add-ons"
            title={<>Everything Weave, Mango, and the fax machine do. Native.</>}
          />
        </div>
        <Stagger className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {COMMS_CHANNELS.map((c, i) => {
            const Icon = ICONS[c.key];
            return (
              <StaggerItem key={c.key} className="h-full">
                <div className="v2-card h-full rounded-2xl p-7">
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#eef3ff]">
                      <Icon className="h-5 w-5 text-[#1a46e5]" />
                    </span>
                    <span className="v2-mono text-[11px] font-medium text-[#a8bcf0]">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="v2-display mt-5 text-[21px] font-semibold leading-tight text-[#0a1633]">{c.name}</div>
                  <p className="mt-2.5 text-[14px] leading-relaxed text-[#3f4d6e]">{c.desc}</p>
                  <ul className="mt-5 space-y-2 border-t border-[#eef3ff] pt-5">
                    {c.points.map((pt) => (
                      <Check key={pt}>{pt}</Check>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>

    {/* AI-native panel */}
    <section className="px-6 pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
            <SectionHead
              dark
              eyebrow="why it has to be native"
              title={<>Legacy phone tools lock your AI out.</>}
              body="An AI receptionist can't answer a phone system it can't reach. A recall agent can't text from an inbox it can't see. Asha's communication layer is AI-native by design — every channel is a surface the workforce can listen on, read from, and act through."
            />
            <Stagger className="space-y-3">
              {[
                'Amy answers because the phones are hers to answer',
                'Stephanie texts from the same inbox your staff uses',
                'Joseph reads the faxes the moment they arrive',
                'Kianna closes balances inside the billing conversation',
              ].map((t) => (
                <StaggerItem key={t}>
                  <div className="rounded-xl border border-white/12 bg-white/5 px-5 py-4 text-[15px] leading-relaxed text-white/85">
                    {t}
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <FadeUp delay={0.15}>
            <div className="mt-16 grid grid-cols-2 gap-y-10 border-t border-white/15 pt-10 md:grid-cols-4">
              <BigStat dark value="7" label="channels, one inbox" />
              <BigStat dark value="∞" label="texts & campaigns" />
              <BigStat dark value="100%" label="calls recorded + transcribed" />
              <BigStat dark value="0" label="add-on subscriptions" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <ClosingCta
      title="Watch one patient's whole story on one screen."
      body="Book a demo and we'll walk a real scenario across calls, texts, forms, fax, and payment — without changing tabs once."
    />
  </V2Layout>
);

export default V2Communications;
